<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] sm:flex sm:items-center sm:justify-between"
    >
      <div class="trezo-card-title">
        <h5 class="mb-0">{{ directT('dashboard.recentOrders') }}</h5>
      </div>
      <div class="trezo-card-subtitle sm:flex sm:items-center">
        <form
          class="relative sm:w-[240px] ltr:sm:mr-[20px] rtl:sm:ml-[20px] my-[13px] sm:my-0"
          @submit.prevent
        >
          <label
            class="leading-none absolute ltr:left-[13px] rtl:right-[13px] text-black dark:text-white mt-px top-1/2 -translate-y-1/2"
          >
            <i class="material-symbols-outlined !text-[20px]"> search </i>
          </label>
          <input
            type="text"
            :placeholder="directT('common.search')"
            v-model="searchTerm"
            class="bg-gray-50 border border-gray-50 h-[36px] text-xs rounded-md w-full block text-black pt-[11px] pb-[12px] ltr:pl-[38px] rtl:pr-[38px] ltr:pr-[13px] ltr:md:pr-[16px] rtl:pl-[13px] rtl:md:pl-[16px] placeholder:text-gray-500 outline-0 dark:bg-[#15203c] dark:text-white dark:border-[#15203c] dark:placeholder:text-gray-400"
          />
        </form>
        <div class="trezo-card-dropdown relative">
          <button
            type="button"
            :class="{
              'trezo-card-dropdown-btn inline-block rounded-md border border-gray-100 py-[5px] md:py-[6.5px] px-[12px] md:px-[19px] transition-all hover:bg-gray-50 dark:border-[#172036] dark:hover:bg-[#0a0e19]': true,
              active: isActive,
            }"
            id="dropdownToggle"
            @click.stop="toggleActive"
          >
            <span
              class="inline-block relative ltr:pr-[17px] ltr:md:pr-[20px] rtl:pl-[17px] rtl:ml:pr-[20px]"
            >
              <template v-if="timeRange === 'day'">{{ directT('dashboard.thisDay') }}</template>
              <template v-else-if="timeRange === 'week'">{{ directT('dashboard.thisWeek') }}</template>
              <template v-else-if="timeRange === 'month'">{{ directT('dashboard.thisMonth') }}</template>
              <template v-else-if="timeRange === 'year'">{{ directT('dashboard.thisYear') }}</template>
              <i
                class="ri-arrow-down-s-line text-lg absolute ltr:-right-[3px] rtl:-left-[3px] top-1/2 -translate-y-1/2"
              ></i>
            </span>
          </button>
          <ul
            class="trezo-card-dropdown-menu transition-all bg-white shadow-3xl rounded-md top-full py-[15px] absolute ltr:left-0 sm:ltr:right-0 rtl:right-0 sm:rtl:left-0 w-[195px] z-[10000] dark:bg-dark dark:shadow-none"
          >
            <li>
              <button
                type="button"
                @click="selectPeriod('day')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisDay') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="selectPeriod('week')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisWeek') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="selectPeriod('month')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisMonth') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="selectPeriod('year')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.thisYear') }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="trezo-card-content">
      <!-- Loading state -->
      <div v-if="isLoading" class="py-8 text-center">
        <div class="animate-spin inline-block w-8 h-8 border-[3px] border-current border-t-transparent text-primary-500 rounded-full" role="status" aria-label="loading">
          <span class="sr-only">{{ directT('common.loading') || 'Loading...' }}</span>
        </div>
      </div>

      <!-- No data state -->
      <div v-else-if="filteredItems.length === 0" class="py-8 text-center text-gray-400 dark:text-gray-500">
        {{ directT('common.noData') || 'No data available' }}
      </div>

      <!-- Data table -->
      <div v-else class="table-responsive overflow-x-auto">
        <table class="w-full">
          <thead class="text-black dark:text-white">
            <tr>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('dashboard.orderId') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('dashboard.customer') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('dashboard.created') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('dashboard.total') }}
              </th>
              <th
                class="font-medium text-left px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tr-md"
              >
                {{ directT('dashboard.status') }}
              </th>
            </tr>
          </thead>
          <tbody class="text-black dark:text-white">
            <tr v-for="item in paginatedItems" :key="item.orderID">
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ item.orderID }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <div class="flex items-center">
                  <span class="block font-medium">
                    {{ item.customer.name }}
                  </span>
                </div>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ item.created }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                ${{ item.total }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <span
                  class="px-[8px] py-[3px] inline-block bg-success-100 dark:bg-[#15203c] text-success-600 rounded-sm font-medium text-xs"
                  v-if="item.status.toLowerCase().includes('ship') || item.status.toLowerCase().includes('deliver')"
                >
                  {{ item.status }}
                </span>
                <span
                  class="px-[8px] py-[3px] inline-block bg-primary-50 dark:bg-[#15203c] text-primary-500 rounded-sm font-medium text-xs"
                  v-else-if="item.status.toLowerCase().includes('confirm') || item.status.toLowerCase().includes('process')"
                >
                  {{ item.status }}
                </span>
                <span
                  class="px-[8px] py-[3px] inline-block bg-warning-50 dark:bg-[#15203c] text-warning-500 rounded-sm font-medium text-xs"
                  v-else-if="item.status.toLowerCase().includes('pend') || item.status.toLowerCase().includes('place')"
                >
                  {{ item.status }}
                </span>
                <span
                  class="px-[8px] py-[3px] inline-block bg-danger-100 dark:bg-[#15203c] text-danger-500 rounded-sm font-medium text-xs"
                  v-else-if="item.status.toLowerCase().includes('cancel') || item.status.toLowerCase().includes('reject')"
                >
                  {{ item.status }}
                </span>
                <span
                  class="px-[8px] py-[3px] inline-block bg-gray-100 dark:bg-[#15203c] text-gray-600 rounded-sm font-medium text-xs"
                  v-else
                >
                  {{ item.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="px-[20px] py-[12px] md:py-[14px] rounded-b-md border-l border-r border-b border-gray-100 dark:border-[#172036] sm:flex sm:items-center justify-between"
      >
        <p class="mb-0 text-sm">
          {{ directT('dashboard.showingResults').replace('{start}', String(startItem + 1)).replace('{end}', String(endItem)).replace('{total}', String(filteredItems.length)) }}
        </p>
        <ol class="mt-[10px] sm:mt-0">
          <li
            class="inline-block mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
            :class="{ disabled: currentPage === 1 }"
          >
            <a
              href="#"
              class="w-[31px] h-[31px] block leading-[29px] relative text-center rounded-md border border-gray-100 dark:border-[#172036] transition-all hover:bg-primary-500 hover:text-white hover:border-primary-500"
              @click.prevent="prevPage"
            >
              <span class="opacity-0"> 0 </span>
              <i
                class="material-symbols-outlined left-0 right-0 absolute top-1/2 -translate-y-1/2"
              >
                chevron_left
              </i>
            </a>
          </li>
          <li
            class="inline-block mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
            v-for="page in totalPages"
            :key="page"
            :class="{ active: currentPage === page }"
          >
            <a
              href="#"
              class="w-[31px] h-[31px] block leading-[29px] relative text-center rounded-md border border-gray-100 dark:border-[#172036] transition-all hover:bg-primary-500 hover:text-white hover:border-primary-500"
              @click.prevent="goToPage(page)"
            >
              {{ page }}
            </a>
          </li>
          <li
            class="inline-block mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
            :class="{ disabled: currentPage === totalPages }"
          >
            <a
              href="#"
              class="w-[31px] h-[31px] block leading-[29px] relative text-center rounded-md border border-gray-100 dark:border-[#172036] transition-all hover:bg-primary-500 hover:text-white hover:border-primary-500"
              @click.prevent="nextPage"
            >
              <span class="opacity-0"> 0 </span>
              <i
                class="material-symbols-outlined left-0 right-0 absolute top-1/2 -translate-y-1/2"
              >
                chevron_right
              </i>
            </a>
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useClickOutside } from "@/utils/useClickOutside";
import { useI18n } from "vue-i18n";
import { getOrders, Order } from "@/services/orderService";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

interface Customer {
  image: string;
  name: string;
}

interface Item {
  orderID: string;
  customer: Customer;
  created: string;
  total: string | number;
  status: string;
}

export default defineComponent({
  name: "RecentOrders",
  setup() {
    const { locale } = useI18n();
    const { isActive, toggleActive } = useClickOutside("dropdownToggle");
    const isLoading = ref(false);
    const timeRange = ref('week'); // Default to week

    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };

    // State for orders data
    const items = ref<Item[]>([]);

    // Function to fetch orders
    const fetchOrders = async (period = 'week') => {
      console.log(`[RecentOrders] Fetching orders for period: ${period}`);
      isLoading.value = true;
      try {
        timeRange.value = period;
        const { orders, error } = await getOrders(null);
        
        if (error) {
          console.error('[RecentOrders] Error fetching orders:', error);
          return;
        }

        console.log(`[RecentOrders] Received ${orders.length} orders`);
        
        // Map order data to the format expected by the component
        items.value = orders.map(order => {
          // Format date from ISO string to readable format
          const createdDate = order.created_at 
            ? new Date(order.created_at).toLocaleDateString(locale.value === 'en' ? 'en-US' : 'he-IL', { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric' 
              }) 
            : '';
            
          // Use the exact status value from the database
          const displayStatus = order.status || "Unknown";
            
          return {
            orderID: order.orderid || `#${order.id.substring(0, 8)}`,
            customer: {
              image: order.customerImage || require("@/assets/images/users/user1.jpg"),
              name: order.name || "Unknown Customer",
            },
            created: createdDate,
            total: order.total ? String(order.total) : "0",
            status: displayStatus
          };
        });

        console.log('[RecentOrders] Orders mapped to display format');

      } catch (err) {
        console.error('[RecentOrders] Exception in fetchOrders:', err);
      } finally {
        isLoading.value = false;
        console.log('[RecentOrders] Loading finished');
      }
    };

    // Initial fetch on component mount
    onMounted(() => {
      console.log('[RecentOrders] Component mounted, initializing data fetch');
      fetchOrders('week');
    });

    // Select time period
    const selectPeriod = (period: string) => {
      console.log(`[RecentOrders] Period selected: ${period}`);
      fetchOrders(period);
      toggleActive();
    };

    const searchTerm = ref("");
    const currentPage = ref(1);
    const itemsPerPage = ref(5);

    const totalPages = computed(() =>
      Math.ceil(filteredItems.value.length / itemsPerPage.value)
    );

    const startItem = computed(
      () => (currentPage.value - 1) * itemsPerPage.value
    );
    const endItem = computed(() =>
      Math.min(startItem.value + itemsPerPage.value, filteredItems.value.length)
    );

    const paginatedItems = computed(() => {
      return filteredItems.value.slice(startItem.value, endItem.value);
    });

    const filteredItems = computed(() => {
      return items.value.filter(
        (item: {
          orderID: string;
          customer: { name: string };
          created: string;
          total: string | number;
          status: string;
        }) =>
          item.orderID.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
          item.customer.name
            .toLowerCase()
            .includes(searchTerm.value.toLowerCase()) ||
          item.created.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
          String(item.total).toLowerCase().includes(searchTerm.value.toLowerCase()) ||
          item.status.toLowerCase().includes(searchTerm.value.toLowerCase())
      );
    });

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
      }
    };

    const goToPage = (page: number) => {
      currentPage.value = page;
    };

    return {
      searchTerm,
      filteredItems,
      paginatedItems,
      currentPage,
      totalPages,
      nextPage,
      prevPage,
      goToPage,
      startItem,
      endItem,
      isActive,
      toggleActive,
      directT,
      isLoading,
      selectPeriod,
      timeRange
    };
  },
});
</script>

<style lang="scss" scoped>
ol {
  li {
    &.active {
      a {
        background-color: #605dff;
        color: #fff;
        border-color: #605dff;
      }
    }
  }
}
</style>
